import { socialProviders } from '@daveyplate/better-auth-ui';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Globe } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthConfig } from '@/lib/api';
import { API_URL, apiErrorFor, fetchWithTimeout, postLocalInit } from '@/lib/api/core';
import { authClient, phoneNumber, signIn, signUp } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useAuthState } from '@/lib/auth-state';
import { pruneSearch } from '@/lib/router-search';
import { callbackAfterSignIn, loginInvitationCopy } from '@/lib/sign-in-redirect';
import { sanitizeRedirectUrl } from '@/lib/url';

export const Route = createFileRoute('/auth/login')({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) =>
    pruneSearch({
      callbackUrl: (search.callbackUrl as string) || undefined,
      mode: (search.mode as string) || undefined,
      error: (search.error as string) || undefined,
      errorDescription: (search.error_description as string) || undefined,
      loginHint: (search.loginHint as string) || (search.login_hint as string) || undefined,
      invitationOrg: (search.invitationOrg as string) || undefined,
      // Landing / deep-link CTAs (e.g. /for/slackbot → ?provider=slack) auto-start
      // that social provider once auth config is loaded.
      provider: typeof search.provider === 'string' && search.provider.trim()
        ? search.provider.trim().toLowerCase()
        : undefined,
      intent:
        search.intent === 'sign-up' || search.intent === 'sign-in'
          ? (search.intent as 'sign-up' | 'sign-in')
          : undefined,
    }),
});

type AuthMethod = 'social' | 'magic-link' | 'whatsapp' | 'email-password';
type AuthIntent = 'sign-in' | 'sign-up';

function getAuthErrorMessage(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null;

  const error = (result as { error?: unknown }).error;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }

  const message = (result as { message?: unknown }).message;
  if (typeof message === 'string' && message.length > 0) return message;

  return null;
}

async function storePasswordCredential(id: string, password: string): Promise<void> {
  if (typeof window === 'undefined') return;
  const PasswordCredentialCtor = (window as unknown as { PasswordCredential?: typeof Credential })
    .PasswordCredential;
  if (!PasswordCredentialCtor || !navigator.credentials?.store) return;
  try {
    const credential = new (PasswordCredentialCtor as unknown as new (init: {
      id: string;
      password: string;
    }) => Credential)({ id, password });
    await navigator.credentials.store(credential);
  } catch {
    // Browsers may reject (e.g. user declined, insecure context) — fail silently.
  }
}

/** Favicon fallback for providers without a bundled icon. */
function ProviderFavicon({ domain }: { domain: string }) {
  const [failed, setFailed] = useState(false);
  const valid =
    domain.length > 0 &&
    domain.length <= 253 &&
    /^[A-Za-z0-9.-]+$/.test(domain);
  if (!valid || failed) return <Globe className="w-4 h-4" />;
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`}
      alt=""
      width={16}
      height={16}
      className="h-4 w-4"
      onError={() => setFailed(true)}
    />
  );
}

function toFriendlyAuthError(message: string | null | undefined): string | null {
  if (!message) return null;

  const normalized = message.toLowerCase().replaceAll('_', ' ').trim();
  if (normalized.includes('account not linked')) {
    return 'This social account matches an existing Lobu user. Sign in with a magic link to verify ownership, then continue.';
  }

  return message;
}

function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, isReady: sessionReady } = useAuthState();
  const {
    data: authConfig,
    isLoading: authConfigLoading,
    error: authConfigError,
  } = useAuthConfig();
  const search = useSearch({ from: '/auth/login' });
  const invitationOrg = search.invitationOrg;

  const [authMethod, setAuthMethod] = useState<AuthMethod>('social');
  const [authIntent, setAuthIntent] = useState<AuthIntent>(search.intent ?? 'sign-in');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(() => search.loginHint ?? '');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneSent, setPhoneSent] = useState(false);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resetPasswordSent, setResetPasswordSent] = useState(false);
  const [localSignInStarted, setLocalSignInStarted] = useState(false);
  /** One-shot guard for `?provider=` deep-link auto social start. */
  const autoProviderStarted = useRef(false);

  const callbackUrl = search.callbackUrl
    ? callbackAfterSignIn(search.callbackUrl)
    : undefined;
  const invitationCopy = loginInvitationCopy(invitationOrg, search.loginHint);
  const isExtensionMode = search.mode === 'extension';
  const socialMergeRequired = search.error === 'account_not_linked';
  const callbackError = toFriendlyAuthError(search.errorDescription || search.error);
  const socialEnabled = Object.values(authConfig?.social ?? {}).some(Boolean);
  const magicLinkEnabled = Boolean(authConfig?.magicLink);
  const whatsappEnabled = Boolean(authConfig?.phone);
  const emailPasswordEnabled = Boolean(authConfig?.emailPassword);
  const passkeyEnabled = Boolean(authConfig?.passkey);
  const singleUserMode = Boolean(authConfig?.singleUserMode);
  const hasUser = Boolean(authConfig?.hasUser);
  // Local installs have no cloud account — instead of the email/password form,
  // they sign in via POST /api/local-init. The server owns the trust boundary
  // (loopback peer; optional LOBU_LOCAL_INIT_ALLOW_PROXY for private Tailscale
  // Serve). On 403 we fall back to the normal form with an explanatory error.
  const showLocalInstall = singleUserMode;
  // After a magic link is sent, replace the whole form with the
  // check-your-email state instead of stacking it below the inputs.
  const showMagicLinkSent = authMethod === 'magic-link' && magicLinkEnabled && magicLinkSent;
  const availableMethods = useMemo<AuthMethod[]>(() => {
    const methods: AuthMethod[] = [];
    if (emailPasswordEnabled) methods.push('email-password');
    if (socialEnabled) methods.push('social');
    if (magicLinkEnabled) methods.push('magic-link');
    if (whatsappEnabled) methods.push('whatsapp');
    return methods;
  }, [emailPasswordEnabled, magicLinkEnabled, socialEnabled, whatsappEnabled]);
  const tabMethods = useMemo<AuthMethod[]>(() => {
    // When email/password is available, social buttons are shown inline above
    // the form and magic-link is accessed via the inline action — neither needs a tab.
    if (emailPasswordEnabled) {
      return availableMethods.filter((method) => method !== 'magic-link' && method !== 'social');
    }
    return availableMethods;
  }, [availableMethods, emailPasswordEnabled]);
  const authConfigErrorMessage =
    authConfigError instanceof Error
      ? authConfigError.message
      : authConfigError
        ? 'Unable to load sign-in options.'
        : null;

  useEffect(() => {
    if (session) {
      const safeUrl = sanitizeRedirectUrl(callbackUrl);
      if (callbackUrl) {
        window.location.href = safeUrl;
      } else {
        navigate({ to: '/' });
      }
    }
  }, [session, navigate, callbackUrl]);

  useEffect(() => {
    if (!authConfig || availableMethods.length === 0) return;
    if (!availableMethods.includes(authMethod)) {
      setAuthMethod(availableMethods[0]);
    }
  }, [authConfig, authMethod, availableMethods]);

  useEffect(() => {
    if (!magicLinkEnabled || !socialMergeRequired) return;
    setAuthMethod('magic-link');
    if (search.loginHint && !email) {
      setEmail(search.loginHint);
    }
  }, [magicLinkEnabled, socialMergeRequired, search.loginHint, email]);

  useEffect(() => {
    if (!search.intent) return;
    setAuthIntent(search.intent);
  }, [search.intent]);

  // Single-user-mode routing: when this is a local-first install, the
  // operator should only ever see one of /sign-in or /sign-up — not both.
  //   - no user yet → force `sign-up` (operator is brand new on this machine)
  //   - user exists → force `sign-in` (sign-up is blocked at the DB hook anyway)
  // Multi-user deployments keep the tab toggle and explicit search-param
  // intent. The override only triggers once `authConfig` has loaded so we
  // don't bounce the operator mid-render.
  useEffect(() => {
    if (!singleUserMode) return;
    if (!authConfig) return;
    const desired: AuthIntent = hasUser ? 'sign-in' : 'sign-up';
    if (authIntent !== desired) setAuthIntent(desired);
  }, [singleUserMode, hasUser, authConfig, authIntent]);

  const getAuthCallbackUrl = () => {
    if (callbackUrl) {
      return sanitizeRedirectUrl(callbackUrl);
    }
    return '/';
  };

  // `quiet` keeps a refused auto-start from showing an error on a page the
  // visitor never asked to sign in from.
  const handleSocialSignIn = async (provider: string, { quiet = false } = {}) => {
    setIsLoading(provider);
    setError(null);
    try {
      const result = await signIn.social({
        provider,
        callbackURL: getAuthCallbackUrl(),
        // First-time signups with no explicit destination land on the home
        // route carrying the provider, so it can route them into that
        // provider's app-install flow. better-auth only follows this for
        // genuine new users; returning users use `callbackURL`. An explicit
        // callbackUrl (invite/deep-link) wins — respect where they were going.
        newUserCallbackURL: callbackUrl
          ? getAuthCallbackUrl()
          : `/?onboard_provider=${encodeURIComponent(provider)}`,
      });
      const authError = getAuthErrorMessage(result);
      if (authError && !quiet) {
        const friendly = toFriendlyAuthError(authError);
        if (friendly?.toLowerCase().includes('magic link')) {
          setAuthMethod('magic-link');
        }
        setError(friendly);
      }
    } catch (err) {
      if (quiet) return;
      setError(
        toFriendlyAuthError(
          err instanceof Error ? err.message : `Failed to sign in with ${provider}`
        )
      );
    } finally {
      setIsLoading(null);
    }
  };

  // Landing CTAs (e.g. lobu.ai/for/slackbot) deep-link with ?provider=slack so the
  // user lands straight in Slack OIDC instead of clicking the social button again.
  // One-shot via ref only — do not gate on isLoading (that re-fires when a manual
  // social click settles and isLoading returns to null).
  // Skip when already signed in or when any auth error is on the URL (including
  // account_not_linked).
  useEffect(() => {
    if (autoProviderStarted.current) return;
    // Wait for session query to settle — session===null is ambiguous while pending.
    if (!sessionReady) return;
    if (session) return;
    if (authConfigLoading || !authConfig) return;
    if (search.error) return;
    const provider = search.provider;
    if (!provider) return;
    autoProviderStarted.current = true;
    // A provider the page lists starts like its button. One it hides (the global
    // page lists only LOBU_GLOBAL_LOGIN_PROVIDERS) is still started for a deep
    // link that needs that exact account, e.g. a chat identity link sent over X;
    // the server decides whether the provider exists, and a refusal leaves the
    // page as it was.
    const listed =
      Object.hasOwn(authConfig.social ?? {}, provider) && !!authConfig.social?.[provider];
    void handleSocialSignIn(provider, { quiet: !listed });
    // handleSocialSignIn is intentionally omitted — one-shot via ref.
    // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot deep-link
  }, [
    authConfig,
    authConfigLoading,
    session,
    sessionReady,
    search.provider,
    search.error,
  ]);

  const requestMagicLink = async () => {
    if (!email) return;

    setIsLoading('magic-link');
    setError(null);
    try {
      await signIn.magicLink({
        email,
        callbackURL: getAuthCallbackUrl(),
      });
      setMagicLinkSent(true);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send magic link');
      return false;
    } finally {
      setIsLoading(null);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    await requestMagicLink();
  };

  const handleEmailPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading('email-password');
    setError(null);
    try {
      if (authIntent === 'sign-up') {
        const result = await signUp.email({
          email,
          password,
          name: fullName || email.split('@')[0] || 'User',
          callbackURL: getAuthCallbackUrl(),
        });
        const authError = getAuthErrorMessage(result);
        if (authError) {
          setError(toFriendlyAuthError(authError));
          return;
        }
        await storePasswordCredential(email, password);
        // Signup provisions membership server-side. Drop any anonymous/pre-signup
        // organization snapshot before membership-gated routes mount.
        await queryClient.invalidateQueries({ queryKey: ['organizations'] });
        // Local-first onboarding nicety: as soon as the operator has a
        // session (signUp auto-signs-in), offer to enroll a passkey so
        // they never type the password again. WebAuthn requires the
        // browser to prompt synchronously from a user gesture — we're
        // inside the form-submit click context here so the prompt fires
        // correctly. Operator can cancel the Touch ID dialog with no
        // harm done; we just fall through to the normal session redirect.
        if (singleUserMode && passkeyEnabled) {
          try {
            await authClient.passkey.addPasskey({
              name: fullName || `${navigator.platform || 'This device'}`,
            });
          } catch {
            // User cancelled the biometric prompt, no passkey support,
            // or the request was interrupted by the session redirect.
            // None of these are fatal — they can still sign in with the
            // password they just typed, and add a passkey later from
            // settings.
          }
        }
      } else {
        const result = await signIn.email({
          email,
          password,
          callbackURL: getAuthCallbackUrl(),
        });
        const authError = getAuthErrorMessage(result);
        if (authError) {
          setError(toFriendlyAuthError(authError));
          return;
        }
        if (!result?.data) {
          setError('Invalid email or password');
          return;
        }
        await storePasswordCredential(email, password);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : getAuthErrorMessage(err);
      setError(message || 'Authentication failed');
    } finally {
      setIsLoading(null);
    }
  };

  const handleLocalSignIn = async () => {
    setError(null);
    setLocalSignInStarted(true);
    setIsLoading('local');
    try {
      // Same loopback handshake the menu bar / CLI use — mints a session for the
      // install's single user with no credential prompt. Loopback + X-Lobu-Client
      // are the trust boundary (server-enforced).
      const res = await postLocalInit();
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error_description?: string }
          | null;
        setError(body?.error_description ?? 'Could not sign in to your local install.');
        return;
      }
      // Session cookie is set; land in the app.
      window.location.href = getAuthCallbackUrl();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in to your local install.');
    } finally {
      setIsLoading(null);
    }
  };

  // Local install → sign in automatically once auth-config resolves. One-shot
  // (guarded by localSignInStarted); on failure the UI shows a Try again button.
  useEffect(() => {
    if (showLocalInstall && authConfig && !session && !localSignInStarted) {
      void handleLocalSignIn();
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot, guarded by localSignInStarted
  }, [showLocalInstall, authConfig, session, localSignInStarted]);

  const handleRequestPasswordReset = async () => {
    if (!email) {
      setError('Enter your email address first to reset your password.');
      return;
    }

    setIsLoading('reset-password-request');
    setError(null);
    try {
      const redirectTo = `${window.location.origin}/auth/reset-password`;
      const response = await fetchWithTimeout(`${API_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, redirectTo }),
      });
      if (!response.ok) {
        throw await apiErrorFor(response);
      }
      setResetPasswordSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset email');
    } finally {
      setIsLoading(null);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;

    setIsLoading('phone');
    setError(null);
    try {
      await phoneNumber.sendOtp({ phoneNumber: phone });
      setPhoneSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setIsLoading(null);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone || !phoneCode) return;

    setIsLoading('verify');
    setError(null);
    try {
      await phoneNumber.verify({
        phoneNumber: phone,
        code: phoneCode,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
      setIsLoading(null);
    }
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center">
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <img src="/lobu-logo.svg" alt="Lobu" className="mx-auto mb-6 h-8 w-auto" />
          <h1 className="text-2xl font-bold">
            {invitationCopy
              ? invitationCopy.title
              : singleUserMode && authIntent === 'sign-up'
                ? 'Set up your local install'
                : singleUserMode
                  ? 'Welcome back'
                  : authIntent === 'sign-up'
                    ? 'Create your account'
                    : 'Sign in'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {invitationCopy
              ? invitationCopy.detail
              : singleUserMode && authIntent === 'sign-up'
                ? 'This account stays on this machine. Email is just an identifier — pick anything you’ll remember.'
                : singleUserMode
                  ? 'Sign in to your local install.'
                  : isExtensionMode
                    ? 'Sign in to connect the browser extension'
                    : authIntent === 'sign-up'
                      ? 'Choose a sign-up method'
                      : 'Choose your preferred sign-in method'}
          </p>
          {singleUserMode && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-muted text-muted-foreground rounded-full text-xs">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              Local install · nothing sent to lobu.ai
            </div>
          )}
          {isExtensionMode && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              Browser Extension
            </div>
          )}
        </div>

        <Card>
          <CardContent>
          {showLocalInstall ? (
            <div className="space-y-4">
              {error ? (
                <>
                  <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error}
                  </div>
                  <button
                    type="button"
                    disabled={isLoading !== null}
                    onClick={handleLocalSignIn}
                    className="flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                  >
                    Try again
                  </button>
                </>
              ) : (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                  <Spinner size="lg" />
                  <span>Signing you into your local install…</span>
                </div>
              )}
            </div>
          ) : (
          <>
          {authConfigLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"><Spinner size="lg" /><span>Loading sign-in options…</span></div>
          )}
          {!authConfigLoading && authConfigErrorMessage && (
            <div className="text-sm text-destructive">
              Failed to load sign-in options. {authConfigErrorMessage}
            </div>
          )}
          {!authConfigLoading && !authConfigErrorMessage && availableMethods.length === 0 && (
            <div className="text-sm text-muted-foreground">
              No sign-in methods are configured. Contact support for access.
            </div>
          )}
          {emailPasswordEnabled && !showMagicLinkSent && (
            <div className="mb-4 flex rounded-lg border p-1">
              <button
                type="button"
                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${authIntent === 'sign-in' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setAuthIntent('sign-in')}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${authIntent === 'sign-up' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setAuthIntent('sign-up')}
              >
                Sign Up
              </button>
            </div>
          )}
          {/* Auth method tabs */}
          {!showMagicLinkSent && tabMethods.length > 1 && (
            <div className="mb-6 flex rounded-lg border p-1">
              {emailPasswordEnabled && (
                <button
                  type="button"
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${authMethod === 'email-password' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setAuthMethod('email-password')}
                >
                  Email + Password
                </button>
              )}
              {socialEnabled && (
                <button
                  type="button"
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${authMethod === 'social' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setAuthMethod('social')}
                >
                  Social
                </button>
              )}
              {!emailPasswordEnabled && magicLinkEnabled && (
                <button
                  type="button"
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${authMethod === 'magic-link' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setAuthMethod('magic-link')}
                >
                  Magic Link
                </button>
              )}
              {whatsappEnabled && (
                <button
                  type="button"
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${authMethod === 'whatsapp' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setAuthMethod('whatsapp')}
                >
                  WhatsApp
                </button>
              )}
            </div>
          )}

          {socialMergeRequired && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              We found an existing account for this identity. To protect the account, verify with a
              magic link before linking social sign-in.
            </div>
          )}

          {callbackError && (
            <div className="mb-4 p-3 text-sm text-destructive bg-destructive/10 rounded-lg">
              {callbackError}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 text-sm text-destructive bg-destructive/10 rounded-lg">
              {error}
            </div>
          )}

          {/* Social Login — inline above email/password, or standalone when no email/password */}
          {!showMagicLinkSent && (emailPasswordEnabled || authMethod === 'social') && socialEnabled && (
            <div className="space-y-3">
              {Object.entries(authConfig?.social ?? {})
                .filter(([, enabled]) => enabled)
                .map(([provider]) => {
                  const known = socialProviders.find((sp) => sp.provider === provider);
                  const Icon = known?.icon;
                  // Older servers omit metadata; retain the provider-key fallback.
                  const meta = authConfig?.socialMeta?.[provider];
                  const displayName =
                    known?.name ?? meta?.name ?? provider.charAt(0).toUpperCase() + provider.slice(1);
                  return (
                    <button
                      key={provider}
                      type="button"
                      onClick={() => handleSocialSignIn(provider)}
                      disabled={isLoading !== null}
                      className="w-full flex items-center justify-center gap-3 h-10 px-4 border border-input rounded-lg bg-background hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50"
                    >
                      {isLoading === provider ? (
                        <Spinner size="lg" />
                      ) : Icon ? (
                        <Icon className="w-4 h-4" />
                      ) : meta?.faviconDomain ? (
                        <ProviderFavicon key={meta.faviconDomain} domain={meta.faviconDomain} />
                      ) : (
                        <Globe className="w-4 h-4" />
                      )}
                      Continue with {displayName}
                    </button>
                  );
                })}
              {emailPasswordEnabled && (
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Email + Password */}
          {!showMagicLinkSent && emailPasswordEnabled && (
            <form onSubmit={handleEmailPassword} className="space-y-4">
              {authIntent === 'sign-up' && (
                <div>
                  <label htmlFor="full-name" className="block text-sm font-medium">
                    Full name
                  </label>
                  <input
                    id="full-name"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Jane Doe"
                    className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}
              <div>
                <label htmlFor="email-password-email" className="block text-sm font-medium">
                  Email address
                </label>
                <input
                  id="email-password-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setResetPasswordSent(false);
                  }}
                  placeholder="you@example.com"
                  required
                  className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div>
                <label htmlFor="email-password-password" className="block text-sm font-medium">
                  Password
                </label>
                <input
                  id="email-password-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a password"
                  required
                  minLength={8}
                  className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading !== null || !email || !password}
                className="flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {isLoading === 'email-password' ? (
                  <>
                    <Spinner size="lg" />
                    <span className="ml-2">
                      {authIntent === 'sign-up' ? 'Creating account...' : 'Signing in...'}
                    </span>
                  </>
                ) : authIntent === 'sign-up' ? (
                  singleUserMode ? 'Create local account' : 'Create Account'
                ) : (
                  'Sign In'
                )}
              </button>
              {authIntent === 'sign-in' && passkeyEnabled && (
                <button
                  type="button"
                  disabled={isLoading !== null}
                  onClick={async () => {
                    setError(null);
                    setIsLoading('passkey');
                    try {
                      // WebAuthn ceremony: browser prompts for Touch ID /
                      // Face ID / security key. The server-side
                      // @better-auth/passkey plugin (auth/index.tsx) verifies
                      // the assertion and mints a session.
                      //
                      // The passkey client returns failure modes (user
                      // cancelled, no credential, verification failed) as
                      // a result.error object rather than throwing. Inspect
                      // both — silent failures otherwise looked like
                      // success because the loading state cleared.
                      const result = await signIn.passkey();
                      if ((result as { error?: { message?: string } })?.error) {
                        const message =
                          (result as { error: { message?: string } }).error
                            .message ?? 'Passkey sign-in failed.';
                        setError(toFriendlyAuthError(message) ?? message);
                        return;
                      }
                      window.location.href = getAuthCallbackUrl();
                    } catch (err) {
                      const message =
                        err instanceof Error ? err.message : String(err);
                      setError(toFriendlyAuthError(message) ?? message);
                    } finally {
                      setIsLoading(null);
                    }
                  }}
                  className="flex h-10 w-full items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  {isLoading === 'passkey' ? (
                    <>
                      <Spinner size="lg" />
                      <span className="ml-2">Waiting for passkey…</span>
                    </>
                  ) : (
                    'Sign in with passkey'
                  )}
                </button>
              )}
              {authIntent === 'sign-in' && magicLinkEnabled && (
                <button
                  type="button"
                  disabled={isLoading !== null || !email}
                  onClick={async () => {
                    const sent = await requestMagicLink();
                    if (sent) setAuthMethod('magic-link');
                  }}
                  className="flex h-10 w-full items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  {isLoading === 'magic-link' ? (
                    <>
                      <Spinner size="lg" />
                      <span className="ml-2">Sending magic link...</span>
                    </>
                  ) : (
                    'Send me a magic link instead'
                  )}
                </button>
              )}
              {authIntent === 'sign-in' && (
                <button
                  type="button"
                  disabled={isLoading !== null || !email}
                  onClick={handleRequestPasswordReset}
                  className="flex h-10 w-full items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  {isLoading === 'reset-password-request' ? (
                    <>
                      <Spinner size="lg" />
                      <span className="ml-2">Sending reset email...</span>
                    </>
                  ) : (
                    'Forgot password?'
                  )}
                </button>
              )}
              {authIntent === 'sign-in' && resetPasswordSent && (
                <p className="text-sm text-muted-foreground">
                  If this email exists, we sent a password reset link.
                </p>
              )}
            </form>
          )}

          {/* Magic Link */}
          {authMethod === 'magic-link' &&
            magicLinkEnabled &&
            (magicLinkSent ? (
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <svg
                    className="h-12 w-12 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h2 className="text-lg font-medium">Check your email</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  We sent a magic link to <strong>{email}</strong>.<br />
                  Click the link in the email to sign in.
                </p>
                <button
                  type="button"
                  className="mt-4 text-sm text-primary hover:underline"
                  onClick={() => setMagicLinkSent(false)}
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <form onSubmit={handleMagicLink} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading !== null || !email}
                  className="flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  {isLoading === 'magic-link' ? (
                    <>
                      <Spinner size="lg" />
                      <span className="ml-2">Sending...</span>
                    </>
                  ) : (
                    'Send Magic Link'
                  )}
                </button>
              </form>
            ))}

          {/* WhatsApp / Phone */}
          {!showMagicLinkSent && authMethod === 'whatsapp' &&
            whatsappEnabled &&
            (!phoneSent ? (
              <form onSubmit={handleSendOTP} className="space-y-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium">
                    WhatsApp Number
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1234567890"
                    required
                    className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Include country code (e.g., +1 for US)
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={isLoading !== null || !phone}
                  className="flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  {isLoading === 'phone' ? (
                    <>
                      <Spinner size="lg" />
                      <span className="ml-2">Sending...</span>
                    </>
                  ) : (
                    'Send Code via WhatsApp'
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div className="text-center text-sm text-muted-foreground">
                  Enter the 6-digit code sent to <strong>{phone}</strong>
                </div>
                <div>
                  <input
                    type="text"
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    required
                    className="block h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-center text-xl tracking-widest placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading !== null || phoneCode.length !== 6}
                  className="flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  {isLoading === 'verify' ? (
                    <>
                      <Spinner size="lg" />
                      <span className="ml-2">Verifying...</span>
                    </>
                  ) : (
                    'Verify & Sign In'
                  )}
                </button>
                <button
                  type="button"
                  className="w-full text-sm text-primary hover:underline"
                  onClick={() => {
                    setPhoneSent(false);
                    setPhoneCode('');
                  }}
                >
                  Use a different number
                </button>
              </form>
            ))}
          </>
          )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
